// ===========================================
// COMPONENTE: CrearImpuestoDialog
// ===========================================
// Dialog para crear un nuevo impuesto en el catálogo

import * as React from 'react'
import { Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ImpuestoBadge } from '@/components/ui/impuesto-badge'
import {
  CATEGORIAS_IMPUESTO_CATALOGO,
  COLORES_IMPUESTO,
  type CategoriaImpuestoCatalogo,
  type ColorImpuesto,
} from '@/types/impuestos'

interface CrearImpuestoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCrear: (data: CrearImpuestoData) => Promise<void>
  nombreInicial?: string
  isCreating?: boolean
}

export interface CrearImpuestoData {
  nombre: string
  nombre_corto?: string
  categoria?: CategoriaImpuestoCatalogo
  color?: ColorImpuesto
}

const COLORES_DISPONIBLES: ColorImpuesto[] = [
  'gray', 'slate', 'red', 'rose', 'orange', 'amber', 'yellow',
  'green', 'emerald', 'teal', 'cyan', 'blue', 'sky', 'indigo', 'violet', 'purple',
]

export function CrearImpuestoDialog({
  open,
  onOpenChange,
  onCrear,
  nombreInicial = '',
  isCreating = false,
}: CrearImpuestoDialogProps) {
  const [nombre, setNombre] = React.useState(nombreInicial)
  const [nombreCorto, setNombreCorto] = React.useState('')
  const [categoria, setCategoria] = React.useState<CategoriaImpuestoCatalogo>('otros')
  const [color, setColor] = React.useState<ColorImpuesto>('gray')

  // Resetear cuando se abre con nuevo nombreInicial
  React.useEffect(() => {
    if (open) {
      setNombre(nombreInicial)
      setNombreCorto(nombreInicial ? nombreInicial.substring(0, 12).toUpperCase() : '')
      setCategoria('otros')
      setColor('gray')
    }
  }, [open, nombreInicial])

  // Auto-generar nombre corto cuando cambia el nombre
  // Referencia al nombre anterior para comparar si el usuario lo modificó manualmente
  const nombreAnteriorRef = React.useRef(nombre)
  const handleNombreChange = (value: string) => {
    const nombreAnterior = nombreAnteriorRef.current
    setNombre(value)
    // Solo auto-generar si el usuario no ha modificado manualmente el nombreCorto
    if (!nombreCorto || nombreCorto === nombreAnterior.substring(0, 12).toUpperCase()) {
      setNombreCorto(value.substring(0, 12).toUpperCase())
    }
    nombreAnteriorRef.current = value
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nombre.trim()) return

    try {
      await onCrear({
        nombre: nombre.trim(),
        nombre_corto: nombreCorto.trim() || undefined,
        categoria,
        color,
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Error creando impuesto:', error)
      // No cerramos el dialog para que el usuario pueda reintentar
    }
  }

  const previewNombreCorto = nombreCorto || nombre.substring(0, 12).toUpperCase()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nuevo Impuesto
            </DialogTitle>
            <DialogDescription>
              Agrega un nuevo impuesto al catálogo de tu organización
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del impuesto *</Label>
              <Input
                id="nombre"
                placeholder="Ej: IVA Mensual, IIBB Buenos Aires..."
                value={nombre}
                onChange={(e) => handleNombreChange(e.target.value)}
                autoFocus
              />
            </div>

            {/* Nombre corto */}
            <div className="space-y-2">
              <Label htmlFor="nombre_corto">Nombre corto (para badges)</Label>
              <Input
                id="nombre_corto"
                placeholder="Máx. 12 caracteres"
                maxLength={12}
                value={nombreCorto}
                onChange={(e) => setNombreCorto(e.target.value.toUpperCase())}
              />
              <p className="text-xs text-muted-foreground">
                Se muestra en las tablas y listas
              </p>
            </div>

            {/* Categoría */}
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={categoria} onValueChange={(v) => setCategoria(v as CategoriaImpuestoCatalogo)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(CATEGORIAS_IMPUESTO_CATALOGO) as [CategoriaImpuestoCatalogo, string][]).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-1.5">
                {COLORES_DISPONIBLES.map((c) => {
                  const colores = COLORES_IMPUESTO[c]
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setColor(c)}
                      className={`w-6 h-6 rounded-md border-2 transition-all ${colores.bg} ${
                        color === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'
                      }`}
                      title={c}
                    />
                  )
                })}
              </div>
            </div>

            {/* Preview */}
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-muted-foreground">Vista previa</Label>
              <div className="flex items-center gap-2">
                <ImpuestoBadge nombre={previewNombreCorto || 'IMPUESTO'} color={color} />
                <span className="text-sm text-muted-foreground">
                  {nombre || 'Nombre del impuesto'}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!nombre.trim() || isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear impuesto
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
